import * as Sentry from '@sentry/nextjs'
import useTranslation from 'next-translate/useTranslation'
import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Trans from 'next-translate/Trans'
import { OrderAddress } from '@lib/api/vendureGraphql'
import { BitpayButtonLogo } from '@components/icons/BitpayButtonLogo'
import { useGetBitpayInvoice } from '@lib/api/bitpay/getBitpayInvoice'
import { useTransitionOrderToState } from '@lib/api/cart/transitionOrderToState'
import { sendEvent } from '@lib/tracking'
import RadioDropdown from '@components/ui/RadioDropdown'
import { FiatPayment } from './FiatPayment'

interface PaymentViewProps {
  shippingInfo: OrderAddress
  billingInfo: OrderAddress
}

export const PaymentView = ({
  shippingInfo,
  billingInfo,
}: PaymentViewProps) => {
  const { t } = useTranslation()
  const { getBitpayInvoice } = useGetBitpayInvoice()
  const { transitionOrderToState } = useTransitionOrderToState()
  const [bitpayInvoiceURL, setBitpayInvoiceURL] = useState<string | null>(null)
  const router = useRouter()

  useEffect(() => {
    async function getInvoice() {
      const { getBitpayInvoice: url } = await getBitpayInvoice()
      setBitpayInvoiceURL(url)
    }
    getInvoice().catch((error) => {
      Sentry.captureException(error)
    })
  }, [getBitpayInvoice])

  const onExit = useCallback(async () => {
    await transitionOrderToState({
      state: 'EnteringShippingMethod',
    })
  }, [transitionOrderToState])

  const [activeRadioDropdown, setActiveRadioDropdown] = useState<
    'Crypto' | 'Fiat' | 'None'
  >('Fiat')

  const handleRadioDropdownClick = (
    toggledPaymentMethod: 'Crypto' | 'Fiat'
  ) => {
    if (toggledPaymentMethod === activeRadioDropdown) {
      setActiveRadioDropdown('None')
    } else {
      setActiveRadioDropdown(toggledPaymentMethod)
      sendEvent({
        event: 'Select_Payment_Method',
        data: {
          button_id: toggledPaymentMethod,
        },
      })
    }
  }

  return (
    <div className="flex flex-col">
      <h2 className="text-blue text-4xl font-archivoBlack my-2">
        {t('checkout:payment_view_title')}
      </h2>
      {process.env.NEXT_PUBLIC_ENABLE_CYBERSOURCE === 'false' && (
        <p className="text-justify">{t('checkout:payment_view_description')}</p>
      )}
      <p className="text-justify my-4">
        <Trans
          i18nKey={'checkout:payment_view_conditions'}
          components={[
            // eslint-disable-next-line jsx-a11y/anchor-has-content, @next/next/no-html-link-for-pages
            <a key={0} href="/terms" className="underline cursor-pointer" />,
            <span key={1} />,
          ]}
        />
      </p>
      {process.env.NEXT_PUBLIC_ENABLE_CYBERSOURCE === 'false' ? (
        <button
          className="mt-7 mb-12 mx-auto lg:ml-0 h-16 w-72 bg-[#1A3B8B] rounded text-[#97AAFF] flex justify-center items-center hover:bg-white/20"
          disabled={bitpayInvoiceURL === null}
          onClick={() => {
            if (bitpayInvoiceURL !== null) {
              sendEvent({
                event: 'Click_Bitpay_Payment',
                data: {},
              })
              void router.push(bitpayInvoiceURL)
            }
          }}
        >
          <BitpayButtonLogo />
        </button>
      ) : (
        <div className="mb-10">
          <RadioDropdown
            title={t('checkout:pay_with_credit_card')}
            toggled={activeRadioDropdown === 'Fiat'}
            onClick={() => handleRadioDropdownClick('Fiat')}
          >
            <FiatPayment />
          </RadioDropdown>
          <RadioDropdown
            title={t('checkout:pay_with_crypto')}
            toggled={activeRadioDropdown === 'Crypto'}
            onClick={() => handleRadioDropdownClick('Crypto')}
          >
            <div className="mt-6">
              <p className="text-justify text-sm">
                {t('checkout:payment_view_description')}
              </p>
              <div className="w-full flex justify-end">
                <button
                  className="h-16 w-full sm:w-72 mt-8 mb-8 bg-[#1A3B8B] rounded text-[#97AAFF] flex justify-center items-center hover:bg-white/20"
                  disabled={bitpayInvoiceURL === null}
                  onClick={() => {
                    if (bitpayInvoiceURL !== null) {
                      sendEvent({
                        event: 'Click_Bitpay_Payment',
                        data: {},
                      })
                      void router.push(bitpayInvoiceURL)
                    }
                  }}
                >
                  <BitpayButtonLogo />
                </button>
              </div>
            </div>
          </RadioDropdown>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-14">
        <div>
          <p className="text-xl mt-2 mb-10 font-bold">
            {t('checkout:shipping_information')}
          </p>
          <p className="my-2">{shippingInfo.customFields?.email}</p>
          <p className="my-2">{shippingInfo.fullName}</p>
          <p className="my-2">
            {shippingInfo.streetLine1} {shippingInfo.city},
          </p>
          {shippingInfo.streetLine2 !== null ? (
            <p className="my-2">{shippingInfo.streetLine2}</p>
          ) : null}
          <p className="my-2">
            {shippingInfo.province} {shippingInfo.postalCode}
          </p>
          <p className="my-2">{shippingInfo.countryCode}</p>
        </div>
        <div>
          <p className="text-xl mt-2 mb-10 font-bold">
            {t('checkout:billing_information')}
          </p>
          <p className="my-2">{billingInfo.customFields?.email}</p>
          <p className="my-2">{billingInfo.fullName}</p>
          <p className="my-2">
            {billingInfo.streetLine1} {billingInfo.city},
          </p>
          {billingInfo.streetLine2 !== null ? (
            <p className="my-2">{billingInfo.streetLine2}</p>
          ) : null}
          <p className="my-2">
            {billingInfo.province} {billingInfo.postalCode}
          </p>
          <p className="my-2">{billingInfo.countryCode}</p>
        </div>
      </div>
      <button
        className="mx-auto lg:ml-0 lg:mr-auto mb-auto h-16 mt-6 lg:mt-0 whitespace-nowrap"
        onClick={onExit}
      >{`< ${t('checkout:back_to_shipping_method')}`}</button>
    </div>
  )
}
